import {
  animate,
  getRelativeOffset,
  ImageScaleViewOpts,
  View,
} from '@play-co/timestep-core/ui';
import { getScreenDimensions } from 'src/lib/utils';

type Match2HighlightOpts = Omit<ImageScaleViewOpts, 'image'>;

interface HighlightItem {
  originalParent: View;
  originalX: number;
  originalY: number;
  originalZ: number;
  view: View;
}

export class Match2Highlight extends View {
  private shade: View;
  private items?: HighlightItem[];
  private showing = false;
  public onTap?: () => void;

  constructor(opts: Match2HighlightOpts = {}) {
    super({
      ...opts,
      clipEvents: false,
    });

    this.shade = new View({
      superview: this,
      backgroundColor: '#000000',
      width: this.style.width + 200,
      height: this.style.height + 200,
      x: -100,
      y: -100,
      opacity: 0.7,
      zIndex: -100,
      canHandleEvents: true,
      clipEvents: false,
    });
    this.shade.onInputStart = () => {
      this.onTap?.();
    };
    this.updateOpts({ visible: false });
  }

  public resize(size: { height: number }) {
    if (!this.showing) return;

    this.updateOpts({
      height: size.height,
    });

    this.shade.updateOpts({
      height: size.height + 200,
    });

    if (this.items) {
      const items = this.items.slice();
      // On resize we need to move the items back to their original positions
      this.clearItems();
      // And then push them again to the new positions
      for (const item of items) {
        this.pushItem(item.view);
      }
    }
  }

  private async animateShow() {
    if (this.showing) return;
    this.showing = true;
    animate.getGroup('Match2Highlight')?.clear();
    this.updateOpts({ visible: true });
    await animate(this.shade, 'Match2Highlight')
      .now({ opacity: 0 })
      .then({ opacity: 0.7 }, 500, animate.easeOut)
      .toPromise();
  }

  private async animateHide() {
    if (!this.showing) return;
    this.showing = false;
    animate.getGroup('Match2Highlight')?.clear();
    await animate(this.shade, 'Match2Highlight')
      .now({ opacity: 0.7 })
      .then({ opacity: 0 }, 300, animate.easeOut)
      .toPromise();
    this.updateOpts({ visible: false });
  }

  public highlighView(view: View) {
    this.pushItem(view);
    this.animateShow();
  }

  public async stopHighlight() {
    await this.animateHide();
    // Clear items (move them back to their original parents) only after the animation is done
    // so the dark area doesn't flash in front of the items during that.
    this.clearItems();
  }

  private pushItem(view: View) {
    const parent = view.getSuperview();
    if (!parent) return;
    if (!this.items) this.items = [];

    // Don't push the same view twice
    if (this.items.find((item) => item.view === view)) return;

    this.items.push({
      originalParent: parent,
      originalX: view.style.x,
      originalY: view.style.y,
      originalZ: view.style.zIndex,
      view: view,
    });

    const pos = getRelativeOffset(view, this);
    view.updateOpts({ x: pos.x, y: pos.y });
    this.addSubview(view);
  }

  private clearItems() {
    if (!this.items) return;
    for (const item of this.items) {
      item.originalParent.addSubview(item.view);
      item.view.updateOpts({
        x: item.originalX,
        y: item.originalY,
        zIndex: item.originalZ,
      });
    }
    this.items = undefined;
  }
}
